import styled from 'styled-components';

/* ------------------------------------------------------------------ */
/* Page shell — same wrapper/container pair as Tickets/Dashboard       */
/* ------------------------------------------------------------------ */

export const TeamMembersWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
  overflow-x: hidden;
`;

export const TeamMembersContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ------------------------------------------------------------------ */
/* Page header                                                         */
/* ------------------------------------------------------------------ */

export const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.md};
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
`;

export const Description = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 4px 0 0 0;
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;

export const InviteUserButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  height: 36px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.1s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.landing.buttonHover};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    justify-content: center;
  }
`;

/* ------------------------------------------------------------------ */
/* Table card — search toolbar sits inside the same card as the table, */
/* matching the HTML mock (one bordered section, search bar on top)    */
/* ------------------------------------------------------------------ */

export const TableCard = styled.section`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

export const TableToolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  padding: ${({ theme }) => theme.landingSpacing.md} ${({ theme }) => theme.landingSpacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.landing.cardBorder};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchSlot = styled.div`
  max-width: 380px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    max-width: none;
  }
`;

export const MemberNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const MemberName = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.landing.text};
`;

export const YouBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: ${({ theme }) => theme.landing.iconBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  color: ${({ theme }) => theme.landing.textSecondary};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 10px;
  font-weight: 600;
`;

export const EmailText = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.landing.text};
`;

export const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: ${({ theme }) => theme.landing.iconBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  color: ${({ theme }) => theme.landing.text};
  font-size: ${({ theme }) => theme.fontSizes.small};
  white-space: nowrap;
`;

export const RemoveButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 500;
  color: ${({ theme, disabled }) =>
    disabled ? theme.landing.textMuted : theme.landing.errorColor};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    text-decoration: underline;
    opacity: 0.85;
  }
`;

export const ClientNameText = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 600;
  color: ${({ theme }) => theme.landing.text};
`;

export const ClientDescriptionText = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 13px;
  color: ${({ theme }) => theme.landing.textSecondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: clamp(160px, 28vw, 480px);
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ClientCodeText = styled.span`
  font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.landing.text};
  background: ${({ theme }) => theme.landing.iconBg};
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
`;

export const RowActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const RowActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover:not(:disabled) {
    background: ${({ $danger, theme }) =>
      $danger ? 'rgba(239, 68, 68, 0.1)' : theme.landing.iconBg};
    color: ${({ $danger, theme }) => ($danger ? theme.landing.errorColor : theme.landing.text)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg[data-spin='true'] {
    animation: spin 1s linear infinite;
  }
`;

/* ------------------------------------------------------------------ */
/* Client logo (favicon-sized)                                          */
/* ------------------------------------------------------------------ */

export const ClientLogoBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  overflow: hidden;
  flex-shrink: 0;
`;

export const ClientLogoImg = styled.img`
  width: 100%;
  height: 100%;
  padding: 3px;
  object-fit: contain;
  display: block;
`;

export const ClientLogoFallback = styled.span`
  color: ${({ theme }) => theme.landing.textSecondary};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
`;

export const ClientCodeCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;
